import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "reamfloraWishlist";

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const parsedValue = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const storeValue = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private or restricted browsing contexts.
  }
};

const normaliseCart = (items) =>
  items
    .filter((item) => item && item.id !== undefined && item.id !== null)
    .map((item) => ({
      id: item.id,
      size: item.size || "",
      color: item.color || "",
      quantity: Math.max(1, Math.floor(Number(item.quantity) || 1)),
    }));

const sameId = (firstId, secondId) => String(firstId) === String(secondId);

export const CartContext = createContext({
  cartItems: [],
  wishlist: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  toggleWishlist: () => {},
  isWishlisted: () => false,
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    normaliseCart(readStoredArray(CART_STORAGE_KEY))
  );
  const [wishlist, setWishlist] = useState(() =>
    readStoredArray(WISHLIST_STORAGE_KEY).filter(
      (id, index, items) =>
        id !== undefined &&
        id !== null &&
        items.findIndex((candidate) => sameId(candidate, id)) === index
    )
  );

  useEffect(() => {
    storeValue(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    storeValue(WISHLIST_STORAGE_KEY, wishlist);
  }, [wishlist]);

  const addToCart = useCallback((productId, quantity = 1, size = "", color = "") => {
    const quantityToAdd = Math.max(1, Math.floor(Number(quantity) || 1));

    setCartItems((currentItems) => {
      const existingItem = currentItems.find(
        (item) =>
          sameId(item.id, productId) &&
          item.size === size &&
          item.color === color
      );

      if (!existingItem) {
        return [
          ...currentItems,
          { id: productId, size, color, quantity: quantityToAdd },
        ];
      }

      return currentItems.map((item) =>
        sameId(item.id, productId) &&
        item.size === size &&
        item.color === color
          ? { ...item, quantity: item.quantity + quantityToAdd }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(
            sameId(item.id, productId) &&
            item.size === size &&
            item.color === color
          )
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1) => {
      const nextQuantity = Math.max(1, Math.floor(Number(quantity) || 1));

      setCartItems((currentItems) =>
        currentItems.map((item) =>
          sameId(item.id, productId) &&
          item.size === size &&
          item.color === color
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const addToWishlist = useCallback((productId) => {
    setWishlist((currentWishlist) =>
      currentWishlist.some((id) => sameId(id, productId))
        ? currentWishlist
        : [...currentWishlist, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlist((currentWishlist) =>
      currentWishlist.filter((id) => !sameId(id, productId))
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((currentWishlist) =>
      currentWishlist.some((id) => sameId(id, productId))
        ? currentWishlist.filter((id) => !sameId(id, productId))
        : [...currentWishlist, productId]
    );
  }, []);

  const isWishlisted = useCallback(
    (productId) => wishlist.some((id) => sameId(id, productId)),
    [wishlist]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlist,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted,
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      isWishlisted,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
